import { useIntl } from 'react-intl';
import { z } from 'zod';
import { IconShieldCheck } from '@rossum/ui/icons/tabler';
import { Box, Button, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import type { PendingApproval } from '../hooks/chatStore';
import { CollapsibleSection } from '../messages/CollapsibleSection';
import { getToolArgumentsSummary } from '../messages/toolCallUtils';

// The API tools (`rossum_api_write`, `master_data_hub_api_write`) are best named by their request line.
const apiCallSchema = z.object({ method: z.string(), path: z.string() });

const describeCall = (input: unknown): string | null => {
  const apiCall = apiCallSchema.safeParse(input);
  if (apiCall.success) return `${apiCall.data.method} ${apiCall.data.path}`;
  return typeof input === 'object' && input !== null
    ? getToolArgumentsSummary(Object.fromEntries(Object.entries(input)))
    : null;
};

type ToolApprovalCardProps = {
  approval: PendingApproval;
  disabled: boolean;
  onRespond: (approved: boolean) => void;
};

// Takes the input's place while the agent waits: the paused run only continues once it is answered.
export const ToolApprovalCard = ({
  approval,
  disabled,
  onRespond,
}: ToolApprovalCardProps) => {
  const intl = useIntl();
  const call = describeCall(approval.input);

  return (
    <Stack
      spacing={1}
      data-cy="chat-tool-approval"
      sx={{
        px: 1.5,
        py: 1,
        border: 1,
        borderColor: 'warning.main',
        borderRadius: borderRadius('m'),
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <SvgIcon fontSize="small" sx={{ color: 'warning.main' }}>
          <IconShieldCheck />
        </SvgIcon>

        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500}>
            {intl.formatMessage({ id: 'features.chat.toolApproval.title' })}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ fontFamily: 'monospace' }}
          >
            {call ? `${approval.toolName}: ${call}` : approval.toolName}
          </Typography>
        </Stack>

        <Button
          size="small"
          color="secondary"
          disabled={disabled}
          onClick={() => onRespond(false)}
        >
          {intl.formatMessage({ id: 'features.chat.toolApproval.deny' })}
        </Button>
        <Button
          variant="contained"
          color="warning"
          size="small"
          disabled={disabled}
          onClick={() => onRespond(true)}
        >
          {intl.formatMessage({ id: 'features.chat.toolApproval.approve' })}
        </Button>
      </Stack>

      {approval.input !== undefined && (
        <CollapsibleSection
          label={intl.formatMessage({
            id: 'features.chat.toolApproval.details',
          })}
        >
          <Box
            component="pre"
            sx={{
              p: 1,
              m: 0,
              mt: 0.5,
              maxHeight: 200,
              overflow: 'auto',
              borderRadius: borderRadius('s'),
              bgcolor: 'background.default',
              border: 1,
              borderColor: 'divider',
              fontSize: theme => theme.typography.caption.fontSize,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: 'text.secondary',
            }}
          >
            {JSON.stringify(approval.input, null, 2)}
          </Box>
        </CollapsibleSection>
      )}
    </Stack>
  );
};
